import { EARTH_RADIUS } from "../../simulation/constants";
import { getEarthAxis } from "../../simulation/geometry";
import type { Vector3 } from "../../simulation/types";
import {
  add,
  clamp,
  cross,
  degreesToRadians,
  dot,
  magnitude,
  normalize,
  radiansToDegrees,
  scale,
} from "../../simulation/vector";
import { dayLengthHours, noonAltitudeDegrees } from "../seasons/geometry";

export type HorizonSeason = "june" | "december";
export const DAY_HOURS = 24;
export const NOON_HOUR = 12;
export const HORIZON_SECONDS_AT_ONE_X = 40;

export interface HorizonSite {
  readonly id: string;
  readonly name: string;
  readonly latitudeDegrees: number;
  readonly blurb: string;
}

export const HORIZON_SITES: readonly HorizonSite[] = [
  {
    id: "north-pole",
    name: "North Pole",
    latitudeDegrees: 90,
    blurb: "The Sun circles the sky and never gets high.",
  },
  {
    id: "temperate-north",
    name: "40°N",
    latitudeDegrees: 40,
    blurb: "A mid-northern latitude — New York, Madrid, Beijing.",
  },
  {
    id: "equator",
    name: "Equator",
    latitudeDegrees: 0,
    blurb: "The Sun can pass almost overhead.",
  },
  {
    id: "pretoria",
    name: "Pretoria",
    latitudeDegrees: -25.75,
    blurb: "Southern mid-latitude — high summer sun in December.",
  },
  {
    id: "south-pole",
    name: "South Pole",
    latitudeDegrees: -90,
    blurb:
      "Opposite the North Pole: when one has midnight sun, the other is dark.",
  },
];

export interface HorizonSnapshot {
  readonly site: HorizonSite;
  readonly season: HorizonSeason;
  readonly hourOfDay: number;
  readonly sitePosition: Vector3;
  readonly localUp: Vector3;
  readonly localEast: Vector3;
  readonly localNorth: Vector3;
  readonly sunDirection: Vector3;
  readonly localSun: Vector3;
  readonly altitudeDegrees: number;
  readonly azimuthDegrees: number;
  readonly noonAltitudeDegrees: number;
  readonly solarDeclinationDegrees: number;
  readonly sunAboveHorizon: boolean;
  readonly dayLengthHours: number;
  readonly sunriseHour: number | null;
  readonly sunsetHour: number | null;
  readonly polarCondition: "midnight-sun" | "polar-night" | null;
}

const POLE_EAST_FALLBACK: Vector3 = [1, 0, 0];

export function sunDirectionForSeason(season: HorizonSeason): Vector3 {
  // Same axis as the Moon / seasons model: north leans toward +Z.
  return season === "june" ? [0, 0, 1] : [0, 0, -1];
}

export function getHorizonSite(id: string): HorizonSite {
  const site = HORIZON_SITES.find((entry) => entry.id === id);
  if (site === undefined) {
    throw new RangeError(`Unknown horizon site: ${id}`);
  }
  return site;
}

function equatorialTowardSun(north: Vector3, sunDirection: Vector3): Vector3 {
  const alongPole = scale(north, dot(north, sunDirection));
  const rejected = add(sunDirection, scale(alongPole, -1));
  if (magnitude(rejected) < 1e-8) {
    return normalize(cross(north, POLE_EAST_FALLBACK));
  }
  return normalize(rejected);
}

export function siteDirection(
  latitudeDegrees: number,
  north: Vector3,
  sunDirection: Vector3,
): Vector3 {
  const latitude = degreesToRadians(latitudeDegrees);
  const towardSun = equatorialTowardSun(north, sunDirection);
  return normalize(
    add(scale(north, Math.sin(latitude)), scale(towardSun, Math.cos(latitude))),
  );
}

export function wrapHour(hour: number): number {
  return ((hour % DAY_HOURS) + DAY_HOURS) % DAY_HOURS;
}

const COMPASS_POINTS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"] as const;

export function compassPoint(azimuthDegrees: number): string {
  const wrapped = ((azimuthDegrees % 360) + 360) % 360;
  const index = Math.round(wrapped / 45) % COMPASS_POINTS.length;
  return COMPASS_POINTS[index] ?? "N";
}

export function formatClockHour(hour: number): string {
  const wrapped = wrapHour(hour);
  const hours = Math.floor(wrapped) % 24;
  const minutes = Math.round((wrapped - Math.floor(wrapped)) * 60) % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function hourAngleRadians(hourOfDay: number): number {
  return ((wrapHour(hourOfDay) - NOON_HOUR) / DAY_HOURS) * Math.PI * 2;
}

export function localSunComponents(
  latitudeDegrees: number,
  declinationDegrees: number,
  hourOfDay: number,
): { east: number; north: number; up: number } {
  const latitude = degreesToRadians(latitudeDegrees);
  const declination = degreesToRadians(declinationDegrees);
  const hourAngle = hourAngleRadians(hourOfDay);
  const sinLat = Math.sin(latitude);
  const cosLat = Math.cos(latitude);
  const sinDec = Math.sin(declination);
  const cosDec = Math.cos(declination);
  const cosHour = Math.cos(hourAngle);
  const sinHour = Math.sin(hourAngle);
  return {
    east: -cosDec * sinHour,
    north: sinDec * cosLat - cosDec * cosHour * sinLat,
    up: sinDec * sinLat + cosDec * cosHour * cosLat,
  };
}

export function getHorizonSnapshot(
  siteId: string,
  season: HorizonSeason,
  hourOfDay = NOON_HOUR,
): HorizonSnapshot {
  const site = getHorizonSite(siteId);
  const hour = wrapHour(hourOfDay);
  const north = getEarthAxis();
  const noonSun = sunDirectionForSeason(season);
  const localUp = siteDirection(site.latitudeDegrees, north, noonSun);
  const sitePosition = scale(localUp, EARTH_RADIUS);
  const eastCross = cross(north, localUp);
  const localEast =
    magnitude(eastCross) < 1e-8
      ? normalize(cross(noonSun, localUp))
      : normalize(eastCross);
  const localNorth = normalize(cross(localUp, localEast));
  const solarDeclinationDegrees = radiansToDegrees(
    Math.asin(clamp(dot(north, noonSun), -1, 1)),
  );
  const local = localSunComponents(
    site.latitudeDegrees,
    solarDeclinationDegrees,
    hour,
  );
  const sunDirection = normalize(
    add(
      add(scale(localEast, local.east), scale(localNorth, local.north)),
      scale(localUp, local.up),
    ),
  );
  const altitudeDegrees = radiansToDegrees(Math.asin(clamp(local.up, -1, 1)));
  const azimuthDegrees =
    (radiansToDegrees(Math.atan2(local.east, local.north)) + 360) % 360;
  const length = dayLengthHours(site.latitudeDegrees, solarDeclinationDegrees);
  const halfDay = length.hours / 2;

  return {
    site,
    season,
    hourOfDay: hour,
    sitePosition,
    localUp,
    localEast,
    localNorth,
    sunDirection,
    localSun: [local.east, local.up, -local.north],
    altitudeDegrees,
    azimuthDegrees,
    noonAltitudeDegrees: noonAltitudeDegrees(
      site.latitudeDegrees,
      solarDeclinationDegrees,
    ),
    solarDeclinationDegrees,
    // Small tolerance keeps the Sun visible at the exact sunrise/sunset instant.
    sunAboveHorizon: altitudeDegrees > -0.01,
    dayLengthHours: length.hours,
    sunriseHour:
      length.polarCondition === null ? wrapHour(NOON_HOUR - halfDay) : null,
    sunsetHour:
      length.polarCondition === null ? wrapHour(NOON_HOUR + halfDay) : null,
    polarCondition: length.polarCondition,
  };
}
